import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { Button } from '@/component-library/primitives/Button/Button';
import Loader from '@/component-library/primitives/Loader/Loader';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import Table from '@/component-library/widgets/Table/Table';
import { selectLoading, selectWorkspaces } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { HttpMethodsEnum, PermissionsEnum } from '@/modules/shared/enums/enums';
import usePermissionChecker from '@/modules/shared/hooks/usePermissionChecker';
import { AppRoutes } from '@/utils/routes/router';

import { useWorkspacesTable } from './useWorkspacesTable';

const Workspaces = () => {
  const { t } = useTranslation();

  const workspaces = useAppSelector(selectWorkspaces);
  const isLoading = useAppSelector(selectLoading);
  const { columns } = useWorkspacesTable();
  const navigate = useNavigate();

  const isActionAllowed = usePermissionChecker(PermissionsEnum.WORKSPACES, HttpMethodsEnum.POST);

  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });

  const [filterValue, setFilterValue] = useState('');

  const filteredData = useMemo(() => {
    return workspaces?.filter((row) =>
      Object.values(row).some(
        (value) => value !== null && value.toString().toLowerCase().includes(filterValue.toLowerCase()),
      ),
    );
  }, [workspaces, filterValue]);

  const handleNavigate = (route: string, isActionAllowed: boolean) => {
    if (!isActionAllowed) {
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    navigate(route);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row items-center mb-4">
        <div className="flex-grow justify-start">
          <SearchField initialValue={filterValue} setSearchValue={setFilterValue} />
        </div>
        <div className="flex justify-end items-center">
          <Button className="!w-[10.5rem]" onClick={() => handleNavigate(AppRoutes.createWorkspace, isActionAllowed)}>
            {t('WorkspacesPage.buttonNewWorkspace')}
          </Button>
        </div>
      </div>
      {isLoading ? (
        <Loader />
      ) : filteredData && filteredData.length > 0 ? (
        <Table columns={columns} data={filteredData ?? []} />
      ) : (
        <NoDataBoard description={t('NoDataBoard.addYourFirstWorkspace')} />
      )}
      <ProvideToasts>
        <Toast
          isOpen={open}
          setOpen={setOpen}
          message={toastInfo.message}
          toastType={toastInfo.toastType as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};

export default Workspaces;
